import { TableCell, TableRow, Tooltip } from '@mui/material';
import { FC, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MarketCategory } from '../../../app/services/api/orderManagementClient';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import { getCategoryIcon } from '../../Storefront/components/CategorySidebar';

type Props = {
  category: MarketCategory;
  parentCategories: MarketCategory[];
  instanceName: string;
};

const CategoryItemRow: FC<Props> = ({ category, parentCategories, instanceName }) => {
  const { t } = useTranslation('categories');
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const [searchParams] = useSearchParams();
  
  const canEdit = hasPermissionTo(['ConfigurationAdmin']) || hasPermissionTo(['CategoryUpdate']);

  const handleRowClick = () => {
    if (!canEdit) return;

    const params = new URLSearchParams();
    const companyId = category.companyId.toString();
    const searchTerm = searchParams.get('searchTerm');

    if (companyId) params.set('companyId', companyId);
    if (searchTerm) params.set('searchTerm', searchTerm);

    navigate(`/categories/edit/${category.categoryId}?${params.toString()}`);
  };

  const truncateText = (text: string, maxLength: number = 30) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getParentCategoryName = (parentId: number) => {
    if (parentId <= 0) return t('none');
    const parent = parentCategories.find(cat => cat.categoryId === parentId);
    return parent ? parent.name : '-';
  };

  return (
    <TableRow
      onClick={canEdit ? handleRowClick : undefined}
      sx={{
        cursor: canEdit ? 'pointer' : 'default',
        '&:hover': canEdit ? {
          backgroundColor: 'action.hover',
        } : undefined,
      }}
    >
      <TableCell>
        <Tooltip 
          title={category.name} 
          placement="top"
        >
          <span>{truncateText(category.name)}</span>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Tooltip 
          title={getParentCategoryName(category.parentId)} 
          placement="top"
        >
          <span>{truncateText(getParentCategoryName(category.parentId))}</span>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Tooltip 
          title={instanceName} 
          placement="top"
        >
          <span>{truncateText(instanceName)}</span>
        </Tooltip>
      </TableCell>
      <TableCell>
        {getCategoryIcon(category.iconKey)}
      </TableCell>
    </TableRow>
  );
};

export default CategoryItemRow;
